"use client";

import { useState, FormEvent } from "react";

interface FormState {
  name: string;
  email: string;
  message: string;
}

export default function ContactForm() {
  const [form, setForm] = useState<FormState>({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        throw new Error("Failed to send message");
      }

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again or email us directly at hello@myndlayer.ai");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-[#0FF4C6]/10 flex items-center justify-center mx-auto mb-6">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0FF4C6" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-white mb-2">Message Sent!</h2>
        <p className="text-[#8892A4]">Thanks for reaching out! We'll get back to you within 48 hours. You can also email us directly at{" "}
          <a href="mailto:hello@myndlayer.ai" className="text-[#0FF4C6] hover:underline">hello@myndlayer.ai</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-[#8892A4] mb-2">
          Name
        </label>
        <input
          id="name"
          name="name"
          type="text"
          required
          value={form.name}
          onChange={handleChange}
          placeholder="Your name"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-[#4A5568] focus:outline-none focus:border-[#0FF4C6]/50 focus:bg-white/[0.06] transition-all duration-200"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-[#8892A4] mb-2">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          value={form.email}
          onChange={handleChange}
          placeholder="you@example.com"
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-[#4A5568] focus:outline-none focus:border-[#0FF4C6]/50 focus:bg-white/[0.06] transition-all duration-200"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-[#8892A4] mb-2">
          Message
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={6}
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us how we can help..."
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-[#4A5568] focus:outline-none focus:border-[#0FF4C6]/50 focus:bg-white/[0.06] transition-all duration-200 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-teal w-full py-3 text-base font-medium disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? "Sending..." : "Send Message"}
      </button>

      <p className="text-center text-sm text-[#4A5568]">
        Or email us directly at{" "}
        <a href="mailto:hello@myndlayer.ai" className="text-[#0FF4C6] hover:underline">
          hello@myndlayer.ai
        </a>
      </p>
    </form>
  );
}